import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { EGG_POOL, PUZZLE } from "@/data/secrets";

const SecretsContext = createContext(null);

const EGGS_KEY = "tff_session_anomalies";
const CLEARANCE_KEY = "tff_clearance";

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const VALID_IDS = EGG_POOL.map((e) => e.id);

// The Tylegn personnel file inside the "Six Seconds" reader is the signature
// anomaly, so it is always live. The remaining anomalies stay random.
const ALWAYS_ON = "personnel";
const ROTATING_IDS = VALID_IDS.filter((id) => id !== ALWAYS_ON);

/** Pick 2-3 anomalies for this visit. Stored in sessionStorage so the
 *  selection is stable while navigating, and resets on a fresh visit. */
const pickEggs = () => {
  try {
    const saved = window.sessionStorage.getItem(EGGS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const clean =
        Array.isArray(parsed) && parsed.filter((id) => VALID_IDS.includes(id));
      // Only reuse a stored selection if it is a valid 2-3 item set.
      if (clean && clean.length >= 2 && clean.length <= 3) {
        if (clean.includes(ALWAYS_ON)) return clean;
        // Older sessions may have been stored without the signature anomaly.
        const repaired = [ALWAYS_ON, ...clean].slice(0, 3);
        try {
          window.sessionStorage.setItem(EGGS_KEY, JSON.stringify(repaired));
        } catch {
          /* ignore */
        }
        return repaired;
      }
    }
  } catch {
    /* ignore */
  }
  const count = Math.random() < 0.5 ? 2 : 3;
  const chosen = [
    ALWAYS_ON,
    ...shuffle(ROTATING_IDS).slice(0, count - 1)
  ];
  try {
    window.sessionStorage.setItem(EGGS_KEY, JSON.stringify(chosen));
  } catch {
    /* ignore */
  }
  return chosen;
};

const normalise = (value) => String(value || "").replace(/[^0-9]/g, "");

export const SecretsProvider = ({ children }) => {
  const [activeEggs] = useState(pickEggs);
  const [fragments, setFragments] = useState([]);
  const [eggsFound, setEggsFound] = useState([]);
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return window.sessionStorage.getItem(CLEARANCE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const [glitchPhase, setGlitchPhase] = useState("idle"); // idle | jitter | blackout
  const [memorial, setMemorial] = useState(false);
  const [overlay, setOverlay] = useState(null); // {kind, ...}
  const [transmitOpen, setTransmitOpen] = useState(false);
  const timers = useRef([]);

  useEffect(
    () => () => {
      timers.current.forEach((t) => window.clearTimeout(t));
    },
    []
  );

  const isEggActive = useCallback(
    (id) => activeEggs.includes(id),
    [activeEggs]
  );

  const markEggFound = useCallback((id) => {
    setEggsFound((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const foundFragment = useCallback((id) => {
    let isNew = false;
    setFragments((prev) => {
      if (prev.includes(id)) return prev;
      isNew = true;
      return [...prev, id];
    });
    return isNew;
  }, []);

  const hasFragment = useCallback(
    (id) => fragments.includes(id),
    [fragments]
  );

  const allFragmentsFound = fragments.length >= PUZZLE.fragments.length;

  const orderedFragments = useMemo(
    () => PUZZLE.fragments.filter((f) => fragments.includes(f.id)),
    [fragments]
  );

  const unlock = useCallback(() => {
    setUnlocked(true);
    try {
      window.sessionStorage.setItem(CLEARANCE_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  const checkCode = useCallback((raw) => {
    const digits = normalise(raw);
    return PUZZLE.accepted.includes(digits);
  }, []);

  /** "accepted" | "near" | "rejected" — lets the transmit line coach the
   *  reader who read 6.13 as six minutes thirteen seconds. */
  const evaluateCode = useCallback((raw) => {
    const digits = normalise(raw);
    if (!digits) return "rejected";
    if (PUZZLE.accepted.includes(digits)) return "accepted";
    if ((PUZZLE.nearMiss || []).includes(digits)) return "near";
    return "rejected";
  }, []);

  const triggerGlitch = useCallback(() => {
    setGlitchPhase((p) => {
      if (p !== "idle") return p;
      timers.current.push(
        window.setTimeout(() => setGlitchPhase("blackout"), 620)
      );
      timers.current.push(
        window.setTimeout(() => setGlitchPhase("idle"), 620 + 6130)
      );
      return "jitter";
    });
  }, []);

  const triggerMemorial = useCallback(() => {
    setMemorial(true);
    timers.current.push(window.setTimeout(() => setMemorial(false), 11000));
  }, []);

  const openOverlay = useCallback((payload) => setOverlay(payload), []);
  const closeOverlay = useCallback(() => setOverlay(null), []);

  const openTransmit = useCallback(() => {
    setTransmitOpen(true);
    window.setTimeout(() => {
      const el = document.getElementById("manual-transmit");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      const input = document.querySelector("[data-testid=transmit-code-input]");
      if (input) input.focus();
    }, 120);
  }, []);

  const value = useMemo(
    () => ({
      activeEggs,
      isEggActive,
      eggsFound,
      markEggFound,
      fragments,
      foundFragment,
      hasFragment,
      allFragmentsFound,
      orderedFragments,
      unlocked,
      unlock,
      checkCode,
      evaluateCode,
      glitchPhase,
      triggerGlitch,
      memorial,
      triggerMemorial,
      overlay,
      openOverlay,
      closeOverlay,
      transmitOpen,
      setTransmitOpen,
      openTransmit
    }),
    [
      activeEggs,
      isEggActive,
      eggsFound,
      markEggFound,
      fragments,
      foundFragment,
      hasFragment,
      allFragmentsFound,
      orderedFragments,
      unlocked,
      unlock,
      checkCode,
      evaluateCode,
      glitchPhase,
      triggerGlitch,
      memorial,
      triggerMemorial,
      overlay,
      openOverlay,
      closeOverlay,
      transmitOpen,
      openTransmit
    ]
  );

  return (
    <SecretsContext.Provider value={value}>{children}</SecretsContext.Provider>
  );
};

export const useSecrets = () => {
  const ctx = useContext(SecretsContext);
  if (!ctx) throw new Error("useSecrets must be used inside SecretsProvider");
  return ctx;
};
